import CircularProgress from '@mui/material/CircularProgress';
import MobileStepper from '@mui/material/MobileStepper';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import TitleAndInformationTooltip from '../titles/TitleAndTooltip';
import DEFAULT_MOBILE_STEPPER_CLASS_NAME from '../../theme/constants/mobile-stepper';

const ShoppingCartStepper = ({
  steps,
  loading,
  onRegisterNavigation,
  showStepper,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const isMobile = useIsMobile();

  const prevStep = useCallback(
    () => setCurrentStep((prev) => Math.max(prev - 1, 0)),
    [],
  );

  const stepCount = steps.length;
  const nextStep = useCallback(
    () => setCurrentStep((prev) => Math.min(prev + 1, stepCount - 1)),
    [stepCount],
  );

  const currentStepId = stepCount > 0 ? steps[currentStep]?.id : undefined;

  useEffect(() => {
    if (loading || stepCount === 0) {
      onRegisterNavigation(null);
      return () => onRegisterNavigation(null);
    }
    onRegisterNavigation({
      prevStep,
      nextStep,
      currentStep,
      stepId: currentStepId,
    });
    return () => onRegisterNavigation(null);
  }, [
    loading,
    stepCount,
    currentStepId,
    onRegisterNavigation,
    prevStep,
    nextStep,
    currentStep,
  ]);

  if (loading || stepCount === 0) {
    return (
      <Stack sx={{ height: '100%', width: '100%', justifyContent: 'center', minHeight: 'inherit' }}>
        <CircularProgress sx={{ alignSelf: 'center' }} />
      </Stack>
    );
  }

  const {
    id,
    title,
    tooltipTitle,
    component: StepComponent,
    props: stepComponentProps,
  } = steps[currentStep];

  return (
    <Stack
      sx={{
        boxShadow: 0,
        '&:before': {
          opacity: 0,
        },
        maxWidth: null,
        width: '100%',
        height: '100%',
        paddingTop: 2,
        paddingBottom: 3,
        px: 2.5,
        minHeight: 250,
      }}
    >
      {showStepper && (
      <MobileStepper
        steps={stepCount}
        activeStep={currentStep}
        position="static"
        variant="dots"
        className={DEFAULT_MOBILE_STEPPER_CLASS_NAME}
        sx={{
          justifyContent: 'center',
          '& .MuiMobileStepper-dot': {
            width: 15,
            height: 15,
            mx: 0.5,
          },
        }}
      />
      )}
      <Stack flexGrow={1} spacing={2} mt={1.5}>
        <TitleAndInformationTooltip
          title={title}
          tooltipTitle={tooltipTitle}
          variant={isMobile ? 'h5' : 'medium_2'}
          fontWeight={600}
          align="center"
          sx={{ display: 'flex', justifyContent: 'center' }}
        />
        <StepComponent
          key={id}
          prevStep={prevStep}
          nextStep={nextStep}
          currentStep={currentStep}
          {...stepComponentProps}
        />
      </Stack>
    </Stack>
  );
};

ShoppingCartStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      component: PropTypes.elementType.isRequired,
      tooltipTitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
      props: PropTypes.shape({}),
    }),
  ).isRequired,
  loading: PropTypes.bool,
  onRegisterNavigation: PropTypes.func,
  showStepper: PropTypes.bool,
};

ShoppingCartStepper.defaultProps = {
  loading: false,
  onRegisterNavigation: () => {},
  showStepper: true,
};

export default ShoppingCartStepper;
